import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  CubeIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Home = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState([]);
  const [clientLogos, setClientLogos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDynamicContent();
  }, []);

  const fetchDynamicContent = async () => {
    try {
      const [testimonialsRes, statsRes, logosRes] = await Promise.all([
        axios.get(`${API_URL}/landing/public/testimonials`),
        axios.get(`${API_URL}/landing/public/stats`),
        axios.get(`${API_URL}/landing/public/client-logos?limit=12`),
      ]);

      setTestimonials(testimonialsRes.data.data || []);
      setStats(statsRes.data.data || []);
      setClientLogos(logosRes.data.data || []);
    } catch (error) {
      console.error('Error fetching dynamic content:', error);
      // Use fallback data if API fails
      setStats([
        { value: '10,000+', label: 'Products Managed' },
        { value: 'Growing', label: 'Happy Customers' },
        { value: '99.9%', label: 'Uptime' },
        { value: '24/7', label: 'Support' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Real-time inventory tracking',
    'Automated low stock alerts',
    'Telegram notifications',
    'Customer management',
    'Financial reporting',
    'Export to Excel/PDF',
    'Multi-currency support',
    '24/7 customer support',
  ];

  // Fallback stats if API doesn't return any
  const displayStats = stats.length > 0 ? stats : [
    { value: '10,000+', label: 'Products Managed' },
    { value: 'Growing', label: 'Happy Customers' },
    { value: '99.9%', label: 'Uptime' },
    { value: '24/7', label: 'Support' },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 transition-colors duration-200">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 gradient-bg dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white animate-fade-in">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Streamline Your Inventory Management
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-primary-100 dark:text-gray-300">
                Modern cloud-based solution for businesses of all sizes. Track products, manage warehouses, and grow your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/request-trial" className="btn-primary bg-white text-primary-600 hover:bg-gray-100 dark:bg-primary-500 dark:text-white dark:hover:bg-primary-600">
                  Start Free 7-Day Trial
                </Link>
                <Link to="/features" className="btn-outline dark:border-gray-500 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white dark:hover:border-gray-400">
                  Explore Features
                </Link>
              </div>
              <p className="mt-6 text-primary-100 dark:text-gray-400 text-sm">
                ✓ No credit card required  ✓ Setup in minutes  ✓ Cancel anytime
              </p>
            </div>

            <div className="hidden lg:block animate-slide-up">
              <div className="bg-white/10 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/20 dark:border-white/10">
                {/* Warehouse/Inventory Visualization */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/50 rounded-lg flex items-center justify-center">
                        <BuildingStorefrontIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Warehouse A</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Real-time Stock Levels</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400 rounded-full text-xs font-medium flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                      Live
                    </span>
                  </div>

                  {/* Inventory Grid - Warehouse Shelves */}
                  <div className="space-y-3 mb-4">
                    {/* Row 1 - High Stock */}
                    <div className="flex items-center space-x-2">
                      <div className="w-20 text-xs font-medium text-gray-600 dark:text-gray-400">Aisle A</div>
                      <div className="flex-1 grid grid-cols-8 gap-1">
                        {[...Array(8)].map((_, i) => (
                          <div
                            key={i}
                            className="h-8 bg-green-500 rounded hover:bg-green-600 transition-colors cursor-pointer relative group"
                            title="In Stock"
                          >
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <CubeIcon className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="w-12 text-xs font-semibold text-green-600 dark:text-green-400">100%</div>
                    </div>

                    {/* Row 2 - Medium Stock */}
                    <div className="flex items-center space-x-2">
                      <div className="w-20 text-xs font-medium text-gray-600 dark:text-gray-400">Aisle B</div>
                      <div className="flex-1 grid grid-cols-8 gap-1">
                        {[...Array(5)].map((_, i) => (
                          <div
                            key={i}
                            className="h-8 bg-blue-500 rounded hover:bg-blue-600 transition-colors cursor-pointer relative group"
                            title="Medium Stock"
                          >
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <CubeIcon className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        ))}
                        {[...Array(3)].map((_, i) => (
                          <div
                            key={i + 5}
                            className="h-8 bg-gray-200 dark:bg-gray-700 rounded"
                          ></div>
                        ))}
                      </div>
                      <div className="w-12 text-xs font-semibold text-blue-600 dark:text-blue-400">62%</div>
                    </div>

                    {/* Row 3 - Low Stock */}
                    <div className="flex items-center space-x-2">
                      <div className="w-20 text-xs font-medium text-gray-600 dark:text-gray-400">Aisle C</div>
                      <div className="flex-1 grid grid-cols-8 gap-1">
                        {[...Array(2)].map((_, i) => (
                          <div
                            key={i}
                            className="h-8 bg-yellow-500 rounded hover:bg-yellow-600 transition-colors cursor-pointer relative group animate-pulse"
                            title="Low Stock"
                          >
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <CubeIcon className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        ))}
                        {[...Array(6)].map((_, i) => (
                          <div
                            key={i + 2}
                            className="h-8 bg-gray-200 dark:bg-gray-700 rounded"
                          ></div>
                        ))}
                      </div>
                      <div className="w-12 text-xs font-semibold text-yellow-600 dark:text-yellow-400">25%</div>
                    </div>

                    {/* Row 4 - Critical Stock */}
                    <div className="flex items-center space-x-2">
                      <div className="w-20 text-xs font-medium text-gray-600 dark:text-gray-400">Aisle D</div>
                      <div className="flex-1 grid grid-cols-8 gap-1">
                        <div className="h-8 bg-red-500 rounded hover:bg-red-600 transition-colors cursor-pointer relative group animate-pulse">
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <CubeIcon className="w-4 h-4 text-white" />
                          </div>
                        </div>
                        {[...Array(7)].map((_, i) => (
                          <div
                            key={i}
                            className="h-8 bg-gray-200 dark:bg-gray-700 rounded"
                          ></div>
                        ))}
                      </div>
                      <div className="w-12 text-xs font-semibold text-red-600 dark:text-red-400">12%</div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-center">
                      <div className="flex items-center justify-center mb-1">
                        <CubeIcon className="w-4 h-4 text-primary-600 dark:text-primary-400 mr-1" />
                        <p className="text-xl font-bold text-gray-900 dark:text-white">2,847</p>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Total Items</p>
                    </div>
                    <div className="text-center border-l border-r border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-center mb-1">
                        <ChartBarIcon className="w-4 h-4 text-green-600 dark:text-green-400 mr-1" />
                        <p className="text-xl font-bold text-gray-900 dark:text-white">94%</p>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">In Stock</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center mb-1">
                        <DocumentTextIcon className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mr-1" />
                        <p className="text-xl font-bold text-gray-900 dark:text-white">23</p>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Low Stock</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {loading ? (
              // Loading skeleton
              [...Array(4)].map((_, index) => (
                <div key={index} className="text-center animate-pulse">
                  <div className="h-10 bg-gray-300 rounded mb-2 mx-auto w-24"></div>
                  <div className="h-4 bg-gray-200 rounded mx-auto w-32"></div>
                </div>
              ))
            ) : (
              displayStats.slice(0, 4).map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-primary-500 dark:text-primary-400 mb-2">
                    {stat.value}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">{stat.label}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="py-16 bg-white dark:bg-gray-950 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Trusted by Leading Businesses
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Trusted by leading businesses across Ethiopia and beyond
            </p>
          </div>
          
          {loading ? (
            // Loading skeleton
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center">
              {[...Array(6)].map((_, index) => (
                <div
                  key={index}
                  className="flex items-center justify-center p-6 bg-gray-50 rounded-lg animate-pulse"
                >
                  <div className="h-8 bg-gray-300 rounded w-24"></div>
                </div>
              ))}
            </div>
          ) : clientLogos.length > 0 ? (
            <div className="relative">
              {/* Scrolling container */}
              <div className="logo-scroll-container">
                <div className="logo-scroll-content">
                  {/* First set of logos */}
                  {clientLogos.map((client, index) => (
                    <div
                      key={`logo-1-${index}`}
                      className="logo-item flex items-center justify-center p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors min-h-[100px] min-w-[180px]"
                      title={client.company_name}
                    >
                      {client.logo_url ? (
                        <div className="relative w-full h-full flex items-center justify-center">
                          <img
                            src={client.logo_url.startsWith('http') ? client.logo_url : `${API_URL.replace(/\/api$/, '')}${client.logo_url}`}
                            alt={client.company_name}
                            className="max-h-20 max-w-full w-auto object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.parentElement.nextElementSibling;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                          <div 
                            className="hidden w-full h-full items-center justify-center"
                            style={{ display: 'none' }}
                          >
                            <span className="text-base font-bold text-gray-400 text-center px-2">
                              {client.company_name}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-base font-bold text-gray-400 text-center px-2">
                          {client.company_name}
                        </span>
                      )}
                    </div>
                  ))}
                  {/* Duplicate set for seamless loop */}
                  {clientLogos.map((client, index) => (
                    <div
                      key={`logo-2-${index}`}
                      className="logo-item flex items-center justify-center p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors min-h-[100px] min-w-[180px]"
                      title={client.company_name}
                    >
                      {client.logo_url ? (
                        <div className="relative w-full h-full flex items-center justify-center">
                          <img
                            src={client.logo_url.startsWith('http') ? client.logo_url : `${API_URL.replace(/\/api$/, '')}${client.logo_url}`}
                            alt={client.company_name}
                            className="max-h-20 max-w-full w-auto object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.parentElement.nextElementSibling;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                          <div 
                            className="hidden w-full h-full items-center justify-center"
                            style={{ display: 'none' }}
                          >
                            <span className="text-base font-bold text-gray-400 text-center px-2">
                              {client.company_name}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-base font-bold text-gray-400 text-center px-2">
                          {client.company_name}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            // Real clients with horizontal sliding animation
            <div className="relative">
              <div className="logo-scroll-container">
                <div className="logo-scroll-content">
                  {/* First set */}
                  {[
                    { name: 'Winner Pipes', initials: 'WP', color: 'bg-blue-100 text-blue-700' },
                    { name: 'Ethioder', initials: 'ET', color: 'bg-green-100 text-green-700' },
                    { name: 'Agrabat Business Group', initials: 'AG', color: 'bg-purple-100 text-purple-700' },
                    { name: 'HG Trading', initials: 'HG', color: 'bg-orange-100 text-orange-700' },
                    { name: 'Marna Import and Export', initials: 'MI', color: 'bg-red-100 text-red-700' },
                  ].map((client, index) => (
                    <div
                      key={`client-1-${index}`}
                      className="logo-item flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-white dark:hover:bg-gray-700 hover:shadow-md transition-all duration-300 min-h-[110px] min-w-[200px] border border-transparent hover:border-gray-200"
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 font-bold text-lg ${client.color}`}>
                        {client.initials}
                      </div>
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 text-center leading-tight">
                        {client.name}
                      </span>
                    </div>
                  ))}
                  {/* Duplicate set for seamless loop */}
                  {[
                    { name: 'Winner Pipes', initials: 'WP', color: 'bg-blue-100 text-blue-700' },
                    { name: 'Ethioder', initials: 'ET', color: 'bg-green-100 text-green-700' },
                    { name: 'Agrabat Business Group', initials: 'AG', color: 'bg-purple-100 text-purple-700' },
                    { name: 'HG Trading', initials: 'HG', color: 'bg-orange-100 text-orange-700' },
                    { name: 'Marna Import and Export', initials: 'MI', color: 'bg-red-100 text-red-700' },
                  ].map((client, index) => (
                    <div
                      key={`client-2-${index}`}
                      className="logo-item flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-white dark:hover:bg-gray-700 hover:shadow-md transition-all duration-300 min-h-[110px] min-w-[200px] border border-transparent hover:border-gray-200"
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 font-bold text-lg ${client.color}`}>
                        {client.initials}
                      </div>
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 text-center leading-tight">
                        {client.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-gray-950 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-600 opacity-10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-indigo-500 opacity-10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Header */}
          <div className="text-center mb-20">
            <span className="inline-block px-4 py-1.5 bg-primary-500/20 text-primary-400 text-sm font-semibold rounded-full border border-primary-500/30 mb-4 tracking-widest uppercase">
              Everything You Need
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Powerful Features,<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-indigo-400">
                Built for Growth
              </span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              A complete inventory platform designed for Ethiopian businesses — from a single shop to multi-warehouse operations.
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

            {/* Card 1 — Large featured */}
            <div className="lg:col-span-2 group relative bg-gradient-to-br from-primary-600 to-primary-800 rounded-3xl p-8 md:p-10 overflow-hidden">
              <div className="absolute top-4 right-4 text-white/10 text-8xl font-black select-none">01</div>
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <CubeIcon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Product & Variant Management</h3>
              <p className="text-primary-100 text-base leading-relaxed mb-6">
                Manage unlimited products with categories, images, barcodes, and full variant support — sizes, colors, SKUs — all in one place. Auto-generate product codes and QR labels instantly.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Barcodes', 'QR Codes', 'SKU Generation', 'Bulk Import'].map(tag => (
                  <span key={tag} className="px-3 py-1 bg-white/15 text-white text-xs rounded-full">{tag}</span>
                ))}
              </div>
            </div>

            {/* Card 2 */}
            <div className="group relative bg-gray-900 border border-gray-800 hover:border-primary-500/50 rounded-3xl p-8 overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-primary-500/10">
              <div className="absolute top-4 right-4 text-white/5 text-8xl font-black select-none">02</div>
              <div className="w-14 h-14 bg-indigo-500/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <BuildingStorefrontIcon className="w-7 h-7 text-indigo-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Multi-Warehouse</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Run multiple warehouse locations with real-time stock levels, inter-warehouse transfers, and per-location reporting — all from one dashboard.
              </p>
            </div>

            {/* Card 3 */}
            <div className="group relative bg-gray-900 border border-gray-800 hover:border-emerald-500/50 rounded-3xl p-8 overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/10">
              <div className="absolute top-4 right-4 text-white/5 text-8xl font-black select-none">03</div>
              <div className="w-14 h-14 bg-emerald-500/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <ChartBarIcon className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Advanced Reports</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Stock movement, profit & loss, sales analysis, purchase reports — export to PDF or Excel with one click.
              </p>
            </div>

            {/* Card 4 — Large featured */}
            <div className="lg:col-span-2 group relative bg-gradient-to-br from-indigo-700 to-indigo-900 rounded-3xl p-8 md:p-10 overflow-hidden">
              <div className="absolute top-4 right-4 text-white/10 text-8xl font-black select-none">04</div>
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <DocumentTextIcon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Sales & Purchase Orders</h3>
              <p className="text-indigo-100 text-base leading-relaxed mb-6">
                Full order lifecycle from creation to fulfillment. Auto-generate PDF invoices, track payments, manage returns, and sync everything with your finance module automatically.
              </p>
              <div className="flex flex-wrap gap-2">
                {['PDF Invoices', 'Payment Tracking', 'Email / WhatsApp', 'Sales Returns'].map(tag => (
                  <span key={tag} className="px-3 py-1 bg-white/15 text-white text-xs rounded-full">{tag}</span>
                ))}
              </div>
            </div>

            {/* Card 5 */}
            <div className="group relative bg-gray-900 border border-gray-800 hover:border-orange-500/50 rounded-3xl p-8 overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-orange-500/10">
              <div className="absolute top-4 right-4 text-white/5 text-8xl font-black select-none">05</div>
              <div className="w-14 h-14 bg-orange-500/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <ShieldCheckIcon className="w-7 h-7 text-orange-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Finance Module</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Accounts receivable, payable, general ledger, and tax configuration — a complete finance system integrated with your inventory.
              </p>
            </div>

            {/* Card 6 */}
            <div className="group relative bg-gray-900 border border-gray-800 hover:border-rose-500/50 rounded-3xl p-8 overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-rose-500/10">
              <div className="absolute top-4 right-4 text-white/5 text-8xl font-black select-none">06</div>
              <div className="w-14 h-14 bg-rose-500/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <UserGroupIcon className="w-7 h-7 text-rose-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">User & Role Management</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Create users with role-based permissions. Full audit trail so you always know who did what and when.
              </p>
            </div>

            {/* Card 7 */}
            <div className="group relative bg-gray-900 border border-gray-800 hover:border-sky-500/50 rounded-3xl p-8 overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-sky-500/10">
              <div className="absolute top-4 right-4 text-white/5 text-8xl font-black select-none">07</div>
              <div className="w-14 h-14 bg-sky-500/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <CloudArrowUpIcon className="w-7 h-7 text-sky-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Cloud & Mobile Ready</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Access from any browser, any device, anywhere. 99.9% uptime with automatic backups — no IT team needed.
              </p>
            </div>

            {/* Card 8 — Telegram highlight */}
            <div className="group relative bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 hover:border-[#2AABEE]/50 rounded-3xl p-8 overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-[#2AABEE]/10">
              <div className="absolute top-4 right-4 text-white/5 text-8xl font-black select-none">08</div>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" style={{ background: 'rgba(42,171,238,0.15)' }}>
                {/* Telegram icon SVG */}
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="#2AABEE">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Telegram Notifications</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Get instant alerts for low stock, new orders, and payments directly on Telegram — no app switching needed.
              </p>
            </div>

          </div>

          {/* Bottom CTA */}
          <div className="mt-16 text-center">
            <Link to="/features" className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-2xl border border-white/20 transition-all duration-200 hover:border-white/40">
              See All Features
              <ArrowRightIcon className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">
              Get started in minutes with our simple 3-step process
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Step 1 */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-100 rounded-full mb-6">
                <span className="text-3xl font-bold text-primary-600">1</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Sign Up & Setup
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Request your free trial and receive your personalized subdomain with login credentials via email within 24 hours.
              </p>
              <div className="inline-flex items-center text-primary-600 font-medium">
                <span>Takes 2 minutes</span>
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </div>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-100 rounded-full mb-6">
                <span className="text-3xl font-bold text-primary-600">2</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Import Your Data
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Add your products, warehouses, and team members. Import existing data from Excel or CSV files with our easy-to-use wizard.
              </p>
              <div className="inline-flex items-center text-primary-600 font-medium">
                <span>Takes 10 minutes</span>
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </div>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-100 rounded-full mb-6">
                <span className="text-3xl font-bold text-primary-600">3</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Start Managing
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Track inventory in real-time, generate reports, and manage your entire operation from one powerful dashboard.
              </p>
              <div className="inline-flex items-center text-primary-600 font-medium">
                <span>Start immediately</span>
                <CheckCircleIcon className="w-4 h-4 ml-2" />
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/request-trial"
              className="btn-primary text-lg px-8 py-4"
            >
              Start Your Free Trial Now
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Choose StockWise Inventory?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Built with modern technology and designed for businesses that want to scale. Our platform grows with you.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl dark:shadow-gray-900/50 border dark:border-gray-800 p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Ready to Get Started?
              </h3>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-semibold text-sm">
                    1
                  </span>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">Sign Up</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">Create your account in seconds</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-semibold text-sm">
                    2
                  </span>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">Setup</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">Add your products and warehouses</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-semibold text-sm">
                    3
                  </span>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">Start Managing</p>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">Track inventory in real-time</p>
                  </div>
                </li>
              </ul>
              <Link
                to="/request-trial"
                className="w-full btn-primary flex items-center justify-center space-x-2"
              >
                <span>Start Your Free Trial</span>
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Don't just take our word for it - hear from businesses like yours
            </p>
          </div>

          {testimonials.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {testimonials.slice(0, 3).map((testimonial) => (
                  <div key={testimonial._id} className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg dark:shadow-gray-900/50 p-8 border border-gray-100 dark:border-gray-800">
                    <div className="flex items-center mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <svg
                          key={i}
                          className="w-5 h-5 text-yellow-400 fill-current"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                        </svg>
                      ))}
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 mb-6 italic">
                      "{testimonial.content}"
                    </p>
                    <div className="flex items-center">
                      {testimonial.image ? (
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full mr-4 object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                          <span className="text-primary-700 font-bold text-lg">
                            {testimonial.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{testimonial.name}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{testimonial.position}, {testimonial.company}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {testimonials.length > 3 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                  {testimonials.slice(3, 5).map((testimonial) => (
                    <div key={testimonial._id} className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg dark:shadow-gray-900/50 p-8 border border-gray-100 dark:border-gray-800">
                      <div className="flex items-center mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <svg
                            key={i}
                            className="w-5 h-5 text-yellow-400 fill-current"
                            viewBox="0 0 20 20"
                          >
                            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                          </svg>
                        ))}
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 mb-6 italic">
                        "{testimonial.content}"
                      </p>
                      <div className="flex items-center">
                        {testimonial.image ? (
                          <img
                            src={testimonial.image}
                            alt={testimonial.name}
                            className="w-12 h-12 rounded-full mr-4 object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                            <span className="text-primary-700 font-bold text-lg">
                              {testimonial.name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white">{testimonial.name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{testimonial.position}, {testimonial.company}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            // Fallback testimonials — Ethiopian customers
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

                {/* Testimonial 1 — Amharic */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg dark:shadow-gray-900/50 p-8 border border-gray-100 dark:border-gray-800">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 mb-6 italic">
                    "ይህ ሶፍትዌር የእኛን የዕቃ አስተዳደር ሙሉ በሙሉ ቀይሮታል። በቀላሉ ምርቶቻችንን ማስተዳደር ችለናል፣ ክምችቱም ሁልጊዜ ትክክለኛ ነው። በጣም እናመሰግናለን!"
                  </p>
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                      <span className="text-primary-700 font-bold text-lg">ብተ</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">ብርሃነ ተስፋዬ</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">የስራ ባለቤት, Winner Pipes</p>
                    </div>
                  </div>
                </div>

                {/* Testimonial 2 — English */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg dark:shadow-gray-900/50 p-8 border border-gray-100 dark:border-gray-800">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 mb-6 italic">
                    "StockWise has been a game changer for our trading business. The real-time stock tracking and purchase order management are exactly what we needed to scale our operations across multiple locations."
                  </p>
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                      <span className="text-green-700 font-bold text-lg">ሙሀ</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">ሙሐመድ ሃሰን</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">General Manager, HG Trading</p>
                    </div>
                  </div>
                </div>

                {/* Testimonial 3 — Mixed Amharic/English */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg dark:shadow-gray-900/50 p-8 border border-gray-100 dark:border-gray-800">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 mb-6 italic">
                    "The finance module, especially accounts receivable and payable, ለኛ ትልቅ ለውጥ አምጥቷል። Before, we used notebooks — now everything is tracked automatically. Highly recommended for any Ethiopian business."
                  </p>
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                      <span className="text-purple-700 font-bold text-lg">ሠነ</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">ሠናይት ነጋሽ</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Finance Manager, Agrabat Business Group</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional testimonials row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">

                {/* Testimonial 4 — Amharic */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg dark:shadow-gray-900/50 p-8 border border-gray-100 dark:border-gray-800">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 mb-6 italic">
                    "ከዚህ ሲስተም ጋር ከተዋወቅን በኋላ የዕቃ ማጣት ችግራችን ሙሉ በሙሉ ቆሟል። Telegram notifications ሲደርሱን ወዲያውኑ እርምጃ መወሰድ ችለናል። ስርዓቱ ቀላልና ፈጣን ነው።"
                  </p>
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                      <span className="text-orange-700 font-bold text-lg">ዳጂ</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">ዳዊት ጅሩ</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Warehouse Supervisor, Marna Import and Export</p>
                    </div>
                  </div>
                </div>

                {/* Testimonial 5 — English */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg dark:shadow-gray-900/50 p-8 border border-gray-100 dark:border-gray-800">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 mb-6 italic">
                    "We import and export goods regularly and needed a system that handles multi-currency and detailed reports. StockWise does it all. The PDF invoices and purchase orders look very professional to our international partners."
                  </p>
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                      <span className="text-red-700 font-bold text-lg">ሂገ</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">ሂሩት ገብረሚካኤል</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">CEO, Ethioder</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 gradient-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Join Businesses Like Winner Pipes, Ethioder & More
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Start your 7-day free trial today. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/request-trial" className="btn-primary bg-white text-primary-600 hover:bg-gray-100">
              Get Started Free
            </Link>
            <Link to="/pricing" className="btn-outline">
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
